// =============================================================================
// responsive.js — SOURCE UNIQUE des points de rupture et de la taille d'écran.
//
// Pourquoi ce fichier (audit responsive iPhone du 10/09/2026) : le dépôt lisait
// `window.innerWidth` à 19 endroits, avec SEPT seuils différents écrits en dur
// (430, 480, 720, 768, 900, 1000, 1024) et, la plupart du temps, SANS écouteur
// de redimensionnement. Conséquences vécues sur iPhone :
//   • la mise en page ne suivait pas la ROTATION — un `useState(() => innerWidth
//     < 768)` est évalué une fois au montage et ne bouge plus jamais ;
//   • deux seuils contradictoires cohabitaient (le rail de module basculait à
//     1000 px, le header à 1024) : entre les deux, l'utilisateur perdait le rail
//     SANS gagner le tiroir ;
//   • le clavier virtuel, qui réduit la fenêtre visible sans déclencher
//     `resize` sur iOS, n'était écouté nulle part.
//
// Règle : tout nouveau code qui a besoin de connaître la taille de l'écran passe
// par ICI. Un composant qui n'a besoin que de STYLE ne doit rien importer du
// tout — une media query CSS est toujours préférable à un rendu React (cf. le
// bloc « SOCLE MOBILE » de App.jsx, qui règle déjà le gros du sujet sans JS).
// =============================================================================
import { useState, useEffect } from "react";

// Points de rupture — alignés sur le parc réel, pas sur des chiffres ronds.
//   sm  375 : iPhone SE / 13 mini (le plus contraint)
//   md  430 : iPhone Plus / Pro Max
//   lg  834 : iPad portrait — au-delà, on peut afficher une mise en page dense
//   xl 1024 : seuil où le header déplie ses boutons de module (Header.js)
export const BP = { sm: 375, md: 430, lg: 834, xl: 1024 };

// Seuil UNIQUE de bascule « navigation au doigt » : le tiroir du header et le
// rail de ModuleSidebar doivent basculer AU MÊME endroit, sans quoi il existe
// une plage de largeurs sans aucune navigation complète.
export const BP_NAV = BP.xl;

// Plus grand « petit côté » d'un téléphone : l'iPhone 16 Pro Max fait 440 px de
// large. L'iPad mini, le plus étroit des iPad, en fait 744 — 480 sépare donc
// franchement les deux familles, avec de la marge pour le prochain modèle.
export const BP_TEL = 480;

/**
 * Classe d'appareil — "tel" | "tablette" | "bureau".
 *
 * VOLONTAIREMENT INSENSIBLE À LA LARGEUR DU VIEWPORT, et c'est tout l'intérêt.
 * Quand un document déborde, iOS Safari élargit le viewport de mise en page au
 * lieu de laisser défiler : `window.innerWidth` et les media queries de largeur
 * mesurent alors cette largeur élargie et franchissent les seuils de bureau.
 * C'est la boucle qui figeait Aramis en mise en page « ordinateur » sur iPhone
 * (constaté le 16/09/2026) : un débordement de quelques dizaines de pixels
 * activait le rail de 270 px, qui élargissait encore la page.
 *
 * On lit donc deux choses que ni l'élargissement ni le pincement ne modifient :
 * le POINTEUR (capacité de l'appareil) et l'ÉCRAN PHYSIQUE (window.screen).
 *
 * Corollaire assumé : un navigateur de BUREAU réduit à 400 px reste "bureau".
 * Le rendu du clavier-souris ne PEUT donc pas bouger — c'est une propriété du
 * prédicat, pas une précaution de style.
 */
export function classeAppareil() {
  if (typeof window === "undefined") return "bureau";
  const grossier = window.matchMedia
    ? window.matchMedia("(pointer:coarse)").matches
    : (navigator.maxTouchPoints || 0) > 0;
  if (!grossier) return "bureau";
  const e = window.screen || {};
  // Le petit côté : screen.width ne pivote pas avec l'appareil sur tous les iOS,
  // prendre le minimum des deux est le seul relevé stable en rotation.
  const court = Math.min(e.width || window.innerWidth, e.height || window.innerHeight);
  return court <= BP_TEL ? "tel" : "tablette";
}

/**
 * Publie la classe d'appareil sur <html data-coquille="…"> et la tient à jour.
 *
 * Sur <html> et non <body> : body porte déjà `zoom` (réglage de taille du
 * texte) et les classes de thème, et un élément zoomé est un contexte piégeux
 * pour tout ce qui se mesure. À appeler UNE fois au démarrage.
 *
 * C'est cet attribut qui permet au CSS de trancher SANS media query de largeur
 * — donc sans se faire mentir par l'élargissement.
 */
export function installerClasseCoquille() {
  if (typeof document === "undefined") return () => {};
  const poser = () => { document.documentElement.dataset.coquille = classeAppareil(); };
  poser();
  window.addEventListener("resize", poser);
  window.addEventListener("orientationchange", poser);
  if (window.visualViewport) window.visualViewport.addEventListener("resize", poser);
  const mq = window.matchMedia && window.matchMedia("(pointer:coarse)");
  if (mq && mq.addEventListener) mq.addEventListener("change", poser);
  return () => {
    window.removeEventListener("resize", poser);
    window.removeEventListener("orientationchange", poser);
    if (window.visualViewport) window.visualViewport.removeEventListener("resize", poser);
    if (mq && mq.removeEventListener) mq.removeEventListener("change", poser);
  };
}

/**
 * La classe d'appareil en React, tenue à jour à la rotation et au branchement
 * d'une souris. À n'utiliser que pour choisir un COMPOSANT différent — pour un
 * simple style, `html[data-coquille="tel"]` en CSS suffit et coûte moins cher.
 */
export function useCoquille() {
  const [classe, setClasse] = useState(classeAppareil);
  useEffect(() => {
    const maj = () => setClasse(classeAppareil());
    window.addEventListener("resize", maj);
    window.addEventListener("orientationchange", maj);
    const mq = window.matchMedia && window.matchMedia("(pointer:coarse)");
    if (mq && mq.addEventListener) mq.addEventListener("change", maj);
    return () => {
      window.removeEventListener("resize", maj);
      window.removeEventListener("orientationchange", maj);
      if (mq && mq.removeEventListener) mq.removeEventListener("change", maj);
    };
  }, []);
  return { classe, estTel: classe === "tel", estTablette: classe === "tablette",
           estBureau: classe === "bureau" };
}

const lire = () => {
  if (typeof window === "undefined") return { w: 1280, h: 800, vh: 800 };
  const vv = window.visualViewport;
  return {
    w: window.innerWidth,
    h: window.innerHeight,
    // Hauteur RÉELLEMENT visible : sur iOS, `innerHeight` ignore le clavier et
    // les barres de Safari, `visualViewport.height` non.
    vh: vv ? vv.height : window.innerHeight,
  };
};

/**
 * Taille de la fenêtre, tenue à jour à la rotation, au redimensionnement ET à
 * l'ouverture du clavier iOS.
 *
 * @returns {{w:number,h:number,vh:number,estMobile:boolean,estTactile:boolean,
 *            clavierOuvert:boolean}}
 */
export function useViewport() {
  const [taille, setTaille] = useState(lire);

  useEffect(() => {
    let brut = null;
    const maj = () => {
      // `requestAnimationFrame` : la rotation iOS émet plusieurs événements de
      // suite, on ne re-rend qu'une fois par image.
      if (brut) return;
      brut = requestAnimationFrame(() => { brut = null; setTaille(lire()); });
    };
    window.addEventListener("resize", maj);
    // `orientationchange` reste nécessaire : sur iOS, `resize` arrive parfois
    // AVANT que les nouvelles dimensions soient publiées.
    window.addEventListener("orientationchange", maj);
    const vv = window.visualViewport;
    if (vv) { vv.addEventListener("resize", maj); vv.addEventListener("scroll", maj); }
    return () => {
      if (brut) cancelAnimationFrame(brut);
      window.removeEventListener("resize", maj);
      window.removeEventListener("orientationchange", maj);
      if (vv) { vv.removeEventListener("resize", maj); vv.removeEventListener("scroll", maj); }
    };
  }, []);

  return {
    ...taille,
    estMobile: taille.w < BP.lg,
    // Le doigt plutôt que la largeur : couvre l'iPhone en paysage et l'iPad,
    // et épargne un navigateur de bureau réduit à 400 px de large.
    estTactile: typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(pointer:coarse)").matches : false,
    // Le clavier iOS ne déclenche pas `resize` : on le déduit de l'écart entre
    // la fenêtre de mise en page et la fenêtre visible.
    clavierOuvert: taille.h - taille.vh > 120,
  };
}

/**
 * Media query en React, pour les cas où le CSS ne suffit pas (choisir un
 * COMPOSANT différent, pas seulement un style).
 * @param {string} requete ex. "(max-width:834px)"
 */
export function useMediaQuery(requete) {
  const [ok, setOk] = useState(
    () => typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(requete).matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(requete);
    const maj = e => setOk(e.matches);
    setOk(mq.matches);
    // `addListener` : repli pour les Safari iOS antérieurs à 14.
    if (mq.addEventListener) mq.addEventListener("change", maj);
    else mq.addListener(maj);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", maj);
      else mq.removeListener(maj);
    };
  }, [requete]);
  return ok;
}
